import {Placement} from '@popperjs/core';
import React, {FC, RefObject, useState} from 'react';

import {KeyboardActionsEnum} from '../../../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Trans} from '../../../../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {
  buildCancelTokenSource,
  CancelError,
  CancelTokenSource,
} from '../../../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {IconButton} from '../../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {KeyboardShortcuts} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {PermissionCheck} from '../../../../../../../srcCommon/permissions/permissionCheck';
import {useComposeCommentOfflinePermission} from '../../../../../../../srcCommon/permissions/useOfflinePermission';
import {PushProps} from '../../../../../../../srcCommon/push/pushContext';
import {
  AsyncData,
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
} from '../../../../../../helpers/asyncDataHelpers';
import {buildTeammateDisplayName} from '../../../../../../helpers/modelHelpers';
import {PermissionResult} from '../../../../../../helpers/permissionHelpers';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {InboxModel} from '../../../../../../models/api/inboxModel';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {ApiSeenByList, SeenByModel} from '../../../../../../models/api/seenByModel';
import {TeammateModel} from '../../../../../../models/api/teammateModel';
import {fetchApiAsync} from '../../../../../../services/http/http';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {OkCancelDialog} from '../../../../../common/dialogs/okCancelDialog';
import {DropdownLoader} from '../../../../../common/dropdowns/dropdownLoader';
import {InviteDropdownTrackersContentContainer} from './inviteDropdownTrackersContentContainer';
import {InviteDropdownTrackersFooter} from './inviteDropdownTrackersFooter';
import {VisualConversationTrackerModel} from './inviteHelpers';

/*
 * Props.
 */

interface InviteDropdownTrackersProps extends PushProps {
  userId: number;
  cellsContext: CellsContext;
  conversation: ConversationModel;
  conversationInboxesTeammateIds: ReadonlyArray<TeammateModel['id']>;
  visualTrackers: ReadonlyArray<VisualConversationTrackerModel>;
  conversationInboxes: ReadonlyArray<InboxModel>;
  isTeamConversation: boolean;
  onInviteClick: Handler;
  removeTracker: HandlerOf<number>;
  onRequestClose?: Handler;
  isUserVisitor: boolean;
  onSubscribeClick: Handler;
  onUnsubscribeClick: Handler;
  anchor: RefObject<HTMLElement>;
  placement: Placement;
}

/*
 * Component.
 */

export const InviteDropdownTrackers: FC<InviteDropdownTrackersProps> = (props) => {
  const offlinePermission = useComposeCommentOfflinePermission();

  const [teammateToRemove, setTeammateToRemove] = useState<TeammateModel | undefined>(undefined);

  const onRemoveTeammateClick: HandlerOf<TeammateModel> = (teammate) => {
    setTeammateToRemove(teammate);
  };

  const onRemoveTeammateCancelClick = () => {
    setTeammateToRemove(undefined);
  };

  const onRemoveTeammateConfirmClick = () => {
    if (teammateToRemove) {
      // Remove the tracker from the conversation.
      const {removeTracker, visualTrackers, onRequestClose} = props;
      removeTracker(teammateToRemove.id);

      // If this was the last tracker, close the window.
      if (
        visualTrackers.every((tracker) =>
          Boolean(tracker.teammate === teammateToRemove || tracker.isUserOrDelegatedTeammate),
        ) &&
        onRequestClose
      ) {
        onRequestClose();
      }
    }

    // Close the confirmation dialog.
    setTeammateToRemove(undefined);
  };

  const [seenBy, setSeenBy] = useState<AsyncData<ReadonlyArray<SeenByModel>>>({
    status: AsyncStatusesEnum.LOADING,
  });
  const [cancelSeenByFetchTokenSource, setCancelSeenByFetchTokenSource] = useState<
    CancelTokenSource | undefined
  >(undefined);

  const fetchSeenByAsync = async () => {
    if (!props.isTeamConversation) {
      return;
    }

    setSeenBy(asyncDataLoading());

    try {
      const path = `${props.conversation.url}/seen_by`;
      const seenByData = await fetchApiAsync(ApiSeenByList, path, {
        cancelToken: cancelSeenByFetchTokenSource ? cancelSeenByFetchTokenSource.token : undefined,
      });

      setSeenBy(asyncDataLoaded(seenByData));
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      setSeenBy(asyncDataFailed(error));
    }
  };

  useInitialEffect(() => {
    const newCancelTokenSource = buildCancelTokenSource();
    setCancelSeenByFetchTokenSource(newCancelTokenSource);
    fetchSeenByAsync();
  });

  const userTracker = props.visualTrackers.find(
    ({teammate}) => teammate.id === props.cellsContext.teammateId,
  );
  const keyHandlers = {
    [KeyboardActionsEnum.SUBSCRIBE]:
      userTracker && !userTracker.hasMuted ? props.onUnsubscribeClick : props.onSubscribeClick,
  };

  const renderDropdownWithKeyboardShortcuts = () => (
    <KeyboardShortcuts handlers={keyHandlers}>{renderDropdown()}</KeyboardShortcuts>
  );

  const renderDropdown = () => (
    <>
      {renderHeader(props, offlinePermission)}
      {renderContent()}
      {renderFooter(props)}
      {maybeRenderRemoveConfirmation(
        teammateToRemove,
        onRemoveTeammateCancelClick,
        onRemoveTeammateConfirmClick,
      )}
    </>
  );

  const renderContent = () => {
    if (props.isTeamConversation && seenBy.status !== AsyncStatusesEnum.LOADED) {
      return <DropdownLoader status={seenBy.status} onRetryClick={fetchSeenByAsync} />;
    }
    // Show participants once all data is loaded.
    return (
      <InviteDropdownTrackersContentContainer
        userId={props.userId}
        cellsContext={props.cellsContext}
        conversation={props.conversation}
        conversationInboxesTeammateIds={props.conversationInboxesTeammateIds}
        visualTrackers={props.visualTrackers}
        onRemoveTeammateClick={onRemoveTeammateClick}
        isUserVisitor={props.isUserVisitor}
        onSubscribeClick={props.onSubscribeClick}
        onUnsubscribeClick={props.onUnsubscribeClick}
        seenBy={seenBy}
      />
    );
  };

  return (
    <Dropdown
      anchor={props.anchor}
      width={320}
      onRequestClose={props.onRequestClose}
      placement={props.placement}
    >
      {renderDropdownWithKeyboardShortcuts()}
    </Dropdown>
  );
};

/*
 * Helpers.
 */

/** Renders header. */
function renderHeader(props: InviteDropdownTrackersProps, offlinePermissionResult: PermissionResult) {
  return (
    <DropdownHeader
      title={<Trans id="invite-header-title">Participants</Trans>}
      rightIcon={maybeRenderInviteButton(props, offlinePermissionResult)}
    />
  );
}

/** Maybe renders invite button for header. */
function maybeRenderInviteButton(
  props: InviteDropdownTrackersProps,
  offlinePermissionResult: PermissionResult,
) {
  if (props.isUserVisitor) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <PermissionCheck
          permission={PermissionsEnum.CONVERSATION_COMMENT}
          namespaces={[props.conversation.namespace]}
          offlinePermission={offlinePermissionResult}
        >
          {(permissionResult) => (
            <IconButton
              iconName="plusCircle"
              label={<Trans id="invitedropdown-plusicon">Add teammates or invite guests</Trans>}
              onClick={props.onInviteClick}
              size={VisualSizesEnum.SMALL}
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
              isDisabled={!permissionResult.isAllowed}
            />
          )}
        </PermissionCheck>
      )}
    </UserThemeConsumer>
  );
}

/** Renders footer. */
function renderFooter(props: InviteDropdownTrackersProps) {
  const {visualTrackers, conversationInboxes, isTeamConversation} = props;

  return (
    <InviteDropdownTrackersFooter
      trackers={visualTrackers}
      conversationInboxes={conversationInboxes}
      isTeamConversation={isTeamConversation}
    />
  );
}

/** Maybe renders remove confirmation dialog. */
function maybeRenderRemoveConfirmation(
  teammateToRemove: TeammateModel | undefined,
  onRemoveCancelClick: Handler,
  onRemoveConfirmClick: Handler,
) {
  if (!teammateToRemove) {
    return null;
  }

  return (
    <OkCancelDialog
      onRequestClose={onRemoveCancelClick}
      title={<Trans id="invite-remove-title">Remove participant?</Trans>}
      okButtonLabel={<Trans id="invite-remove-ok">Remove</Trans>}
      onOkClick={onRemoveConfirmClick}
    >
      <Trans id="invite-remove-content">
        <strong>{buildTeammateDisplayName(teammateToRemove)}</strong> will lose access to this conversation.
      </Trans>
    </OkCancelDialog>
  );
}
